import type { LiveModelChoice, ModelConsumerResponse } from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { ProviderOutput } from '@crowdex/provider-contracts';
import { InlineMessage } from '@crowdex/ui/components/feedback';
import { LoaderCircle, Mic, Play, Volume2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  AvailableModels,
  type ListedModel,
  NoModels,
  VOICE_OUTPUTS,
} from '@/components/settings-page/model-settings';
import {
  SettingRow,
  SettingSelect,
  SettingsGroup,
  settingButtonClass,
} from '@/components/settings-page/setting-controls';
import {
  readDictationSettings,
  type DictationSettings as Settings,
  writeDictationSettings,
} from '@/features/dictation/settings';
import { isSpeechLanguage, SPEECH_LANGUAGES } from '@/features/dictation/speech/language';
import { callProvider } from '@/features/dictation/transports';
import { usePluginWorkbench } from '@/features/plugins/use-plugin-workbench';
import { monitorSpeech } from '@/features/voice-call/monitor-speech';
import { speakAs } from '@/features/voice-call/voice-sample';
import { isElectronDesktop } from '@/lib/electron-bridge';
import { useRouter } from '@/lib/navigation';
import { currentShortcut, shortcutKeycaps } from '@/lib/settings-page/keyboard-shortcuts';

type Provider = { id: string; title: string; chosen: boolean };
type Choice = LiveModelChoice;
type Model = ListedModel;
type ModelChoice = NonNullable<Choice['listen']>;

/** The engine value of the core's pipeline (a provider's is its plugin id). */
const PIPELINE = '__pipeline';
/** A model and its provider, as one value of a picker. */
const modelValue = (choice: ModelChoice | undefined) =>
  choice?.model === undefined ? DEFAULT : `${choice.provider ?? ''}\n${choice.model}`;
const modelChoice = (value: string): ModelChoice | undefined => {
  if (value === DEFAULT) return undefined;
  const [provider, model] = value.split('\n');
  return { ...(provider ? { provider } : {}), ...(model ? { model } : {}) };
};

/** The select value for the provider's own voice (Radix has no empty value). */
const DEFAULT = '__default';
const message = (reason: unknown) => (reason instanceof Error ? reason.message : String(reason));
type Voices = ProviderOutput<'realtime.v1', 'voices.list'>;

/** What a voice consumer (`live`, `dictation`) uses, saved as the user changes it. */
function useVoiceChoice(consumer: 'live' | 'dictation') {
  const path = `/api/models/consumers/${consumer}`;
  const [choice, setChoice] = useState<Choice | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Counts the choices saved, for what depends on them on the server. */
  const [saved, setSaved] = useState(0);
  useEffect(() => {
    let live = true;
    apiFetch<ModelConsumerResponse>(path).then(
      (answer) => live && setChoice((answer.choice as Choice | null) ?? {}),
      (reason: unknown) => live && setError(message(reason)),
    );
    return () => {
      live = false;
    };
  }, [path]);
  const save = (next: Choice) => {
    setChoice(next);
    setError(null);
    void apiFetch(path, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(Object.keys(next).length === 0 ? null : next),
    }).then(
      () => setSaved((count) => count + 1),
      (reason: unknown) => setError(message(reason)),
    );
  };
  return { choice, error, save, saved };
}

/** Settings › Voice: what voice calls with agents (Live) and dictation run on. */
export function VoiceSettingsSection({ phone = false }: { phone?: boolean }) {
  const router = useRouter();
  const [providers, setProviders] = useState<Provider[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    apiFetch<{ contracts: { contract: string; providers: Provider[] }[] }>('/api/providers').then(
      (listing) =>
        setProviders(
          listing.contracts.find((entry) => entry.contract === 'realtime.v1')?.providers ?? [],
        ),
      (reason: unknown) => setError(message(reason)),
    );
  }, []);
  const [models, setModels] = useState<Model[]>([]);
  useEffect(() => {
    apiFetch<{ models: Model[] }>('/api/models').then(
      (listing) => setModels(listing.models),
      () => setModels([]),
    );
  }, []);
  const making = (output: string) => models.filter((model) => model.output.includes(output));
  const canPipeline = making('realtime-transcription').length > 0;
  const live = useVoiceChoice('live');
  // The voices of Live's provider, which differ from one provider to another.
  const [voices, setVoices] = useState<Voices | null>(null);
  const liveProvider = live.choice?.provider;
  // biome-ignore lint/correctness/useExhaustiveDependencies(live.saved): the server lists the voices of the choice saved, so each save lists them again.
  useEffect(() => {
    void liveProvider;
    let current = true;
    callProvider<Voices>('realtime.v1', 'voices.list', {}, 'live').then(
      (listed) => current && setVoices(listed),
      () => current && setVoices(null),
    );
    return () => {
      current = false;
    };
  }, [liveProvider, live.saved]);
  // The call views of the user's plugins (Settings has no workspace).
  const callViews = usePluginWorkbench(null).callViews;
  const dictation = useVoiceChoice('dictation');
  const [settings, setSettings] = useState<Settings>(readDictationSettings);
  const providerOptions = (providers ?? []).map(({ id, title }) => ({ value: id, label: title }));
  /** The provider a consumer runs on: its own, else the first the user has. */
  const providerOf = (choice: Choice | null) =>
    providers?.find((provider) => provider.id === choice?.provider)?.id ?? providers?.[0]?.id ?? '';
  /** What a consumer runs on: the pipeline, or its provider. */
  const engineOf = (choice: Choice | null) =>
    choice?.mode === 'pipeline' || (providerOptions.length === 0 && canPipeline)
      ? PIPELINE
      : providerOf(choice);
  const engineOptions = [
    ...providerOptions,
    ...(canPipeline ? [{ value: PIPELINE, label: 'Pipeline' }] : []),
  ];
  /** Picks a consumer's engine: its voices and models are not the previous engine's. */
  const pickEngine = (consumer: typeof live, engine: string) => {
    const { voice: _voice, mode: _mode, provider: _provider, ...rest } = consumer.choice ?? {};
    consumer.save(
      engine === PIPELINE ? { ...rest, mode: 'pipeline' } : { ...rest, provider: engine },
    );
  };
  /** A model picker of the pipeline. */
  const modelSelect = (
    consumer: typeof live,
    key: 'listen' | 'think' | 'speak',
    output: string,
    title: string,
    description: string,
  ) => (
    <SettingRow description={phone ? undefined : description} key={key} title={title}>
      <SettingSelect
        label={title}
        onValueChange={(value) => {
          const { [key]: _previous, ...rest } = consumer.choice ?? {};
          const picked = modelChoice(value);
          // The speech model's voices are not the previous one's.
          const kept = key === 'speak' ? (({ voice: _voice, ...others }) => others)(rest) : rest;
          consumer.save(picked === undefined ? kept : { ...kept, [key]: picked });
        }}
        options={[
          { value: DEFAULT, label: 'Automatic' },
          ...making(output).map((model) => ({
            value: modelValue({ provider: model.provider, model: model.id }),
            label: `${model.name} · ${model.providerTitle}`,
          })),
        ]}
        value={modelValue(consumer.choice?.[key])}
      />
    </SettingRow>
  );
  const livePipeline = engineOf(live.choice) === PIPELINE;
  const dictationPipeline = engineOf(dictation.choice) === PIPELINE;
  const engineLabel = (value: string) =>
    value === PIPELINE
      ? 'Pipeline'
      : (providerOptions.find((option) => option.value === value)?.label ?? 'None');
  const voiceOptions = [
    {
      value: DEFAULT,
      label: voices?.default
        ? (voices.voices.find((voice) => voice.id === voices.default)?.name ?? voices.default)
        : 'Provider default',
    },
    ...(voices?.voices ?? []).map((voice) => ({ value: voice.id, label: voice.name })),
    // A voice chosen before, while the provider does not list it (yet).
    ...(live.choice?.voice !== undefined &&
    !(voices?.voices ?? []).some((voice) => voice.id === live.choice?.voice)
      ? [{ value: live.choice.voice, label: live.choice.voice }]
      : []),
  ];
  const dictateKeys = currentShortcut('dictate');
  return (
    <>
      {[error, live.error, dictation.error]
        .filter((value) => value !== null)
        .map((value) => (
          <InlineMessage className="mb-4" key={value} tone="error">
            {value}
          </InlineMessage>
        ))}
      {providers !== null && providers.length === 0 && !canPipeline && (
        <div className="mb-6">
          <NoModels title="No voice available" />
        </div>
      )}
      <SettingsGroup title="Live">
        <SettingRow title="Engine">
          {engineOptions.length > 1 ? (
            <SettingSelect
              label="Live engine"
              onValueChange={(engine) => pickEngine(live, engine)}
              options={engineOptions}
              value={engineOf(live.choice)}
            />
          ) : (
            <span className="text-muted-foreground text-sm">
              {engineLabel(engineOf(live.choice))}
            </span>
          )}
        </SettingRow>
        {livePipeline && (
          <>
            {modelSelect(
              live,
              'listen',
              'realtime-transcription',
              'Listening',
              'Hears you, live, and tells when you are done speaking',
            )}
            {modelSelect(
              live,
              'think',
              'text',
              'Answering',
              'The fast model that answers in the call',
            )}
            {modelSelect(live, 'speak', 'speech', 'Speech', 'Reads the answers aloud')}
          </>
        )}
        <SettingRow title="Voice">
          <SettingSelect
            label="Voice"
            onValueChange={(voice) => {
              const { voice: _previous, ...rest } = live.choice ?? {};
              live.save(voice === DEFAULT ? rest : { ...rest, voice });
            }}
            options={voiceOptions}
            value={live.choice?.voice ?? DEFAULT}
          />
          <TryVoice phone={phone} voice={live.choice?.voice ?? voices?.default ?? undefined} />
        </SettingRow>
        {callViews.length > 0 && (
          <SettingRow title="Avatar">
            <SettingSelect
              label="Avatar"
              onValueChange={(view) => {
                const { view: _previous, ...rest } = live.choice ?? {};
                live.save(view === DEFAULT ? rest : { ...rest, view });
              }}
              options={[
                { value: DEFAULT, label: callViews[0]?.title ?? 'Automatic' },
                ...callViews.slice(1).map((view) => ({ value: view.plugin, label: view.title })),
                { value: 'none', label: 'None' },
              ]}
              value={
                live.choice?.view === callViews[0]?.plugin
                  ? DEFAULT
                  : (live.choice?.view ?? DEFAULT)
              }
            />
          </SettingRow>
        )}
      </SettingsGroup>
      <SettingsGroup title="Dictation">
        <SettingRow title="Engine">
          {engineOptions.length > 1 ? (
            <SettingSelect
              label="Dictation engine"
              onValueChange={(engine) =>
                dictation.save(engine === PIPELINE ? { mode: 'pipeline' } : { provider: engine })
              }
              options={engineOptions}
              value={engineOf(dictation.choice)}
            />
          ) : (
            <span className="text-muted-foreground text-sm">
              {engineLabel(engineOf(dictation.choice))}
            </span>
          )}
        </SettingRow>
        {dictationPipeline &&
          modelSelect(
            dictation,
            'listen',
            'realtime-transcription',
            'Listening',
            'Hears you, live',
          )}
        <SettingRow title="Language">
          <SettingSelect
            label="Dictation language"
            onValueChange={(value) => {
              if (value !== 'system' && !isSpeechLanguage(value)) return;
              const next: Settings = { ...settings, speechLanguage: value };
              setSettings(next);
              void writeDictationSettings(next).catch(() => undefined);
            }}
            options={[
              { value: 'system', label: 'Automatic' },
              ...SPEECH_LANGUAGES.map(({ code, label }) => ({ value: code, label })),
            ]}
            value={settings.speechLanguage}
          />
        </SettingRow>
        {!phone && isElectronDesktop() && dictateKeys !== undefined && (
          <SettingRow
            description="Brings Crowdex forward and dictates, from any app"
            title="Global shortcut"
          >
            <button
              aria-label="Change the dictation shortcut"
              className="rounded-md border border-foreground/15 px-1.5 py-0.5 text-muted-foreground text-xs hover:text-foreground"
              onClick={() => router.replace('/settings?section=keyboard-shortcuts')}
              type="button"
            >
              {shortcutKeycaps(dictateKeys).join(' ')}
            </button>
          </SettingRow>
        )}
        {!phone && (
          <SettingRow title="Try the microphone">
            <MicrophoneTest />
          </SettingRow>
        )}
      </SettingsGroup>
      {phone && <MicrophoneTest phone />}
      {/*
       * The voices offered. Mistral's (Voxtral) come through Crowpus' /mistral routes;
       * TODO(crowpus-voice): ElevenLabs' still come with its own key (its plugin's connection)
       * until Crowpus has a voice route, then they are listed from Crowpus like the others.
       */}
      <AvailableModels
        models={models.filter((model) =>
          model.output.some((output) => VOICE_OUTPUTS.includes(output)),
        )}
        title="Available voices"
      />
    </>
  );
}

const SAMPLE = 'Hello! This is how I sound in a call.';

/** Says a sentence with the chosen voice (the user's speech provider). */
function TryVoice({ voice, phone }: { voice: string | undefined; phone: boolean }) {
  const [state, setState] = useState<'idle' | 'loading' | 'playing'>('idle');
  const stop = useRef<AbortController | null>(null);
  useEffect(() => () => stop.current?.abort(), []);
  const play = () => {
    if (state !== 'idle') {
      stop.current?.abort();
      setState('idle');
      return;
    }
    const controller = new AbortController();
    stop.current = controller;
    setState('loading');
    void speakAs(SAMPLE, voice, {
      signal: controller.signal,
      onStart: () => setState('playing'),
      onLevel: () => undefined,
    })
      .catch(() => undefined)
      .finally(() => setState('idle'));
  };
  return (
    <button
      aria-label={state === 'idle' ? 'Try the voice' : 'Stop the voice'}
      className={
        phone
          ? 'flex size-9 items-center justify-center rounded-full bg-control'
          : settingButtonClass()
      }
      onClick={play}
      type="button"
    >
      {state === 'loading' ? (
        <LoaderCircle className="size-3.5 animate-spin" />
      ) : phone ? (
        <Play className="size-3.5" />
      ) : (
        <Volume2 className="size-3.5" />
      )}
      {!phone && (state === 'idle' ? 'Try' : 'Stop')}
    </button>
  );
}

/** Listens for a few seconds and shows how loud the microphone hears the user. */
function MicrophoneTest({ phone = false }: { phone?: boolean }) {
  const [level, setLevel] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const start = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setLevel(0);
      const monitor = monitorSpeech(stream, () => undefined, setLevel);
      window.setTimeout(() => {
        monitor?.close();
        for (const track of stream.getTracks()) track.stop();
        setLevel(null);
      }, 4_000);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The microphone is not available.');
    }
  };
  return (
    <span className={phone ? 'flex w-full flex-col items-center gap-2' : 'flex items-center gap-2'}>
      {level !== null && (
        <span
          className="flex h-1.5 w-20 overflow-hidden rounded-full bg-control"
          title="How loud the microphone hears you"
        >
          <span
            className="bg-success transition-[width]"
            style={{ width: `${Math.min(100, level * 100)}%` }}
          />
          <span className="sr-only">Listening</span>
        </span>
      )}
      {error && <span className="text-destructive text-xs">{error}</span>}
      <button
        className={
          phone
            ? 'flex h-12 w-full items-center justify-center gap-2 rounded-2xl border bg-card text-base text-foreground'
            : settingButtonClass()
        }
        disabled={level !== null}
        onClick={() => void start()}
        type="button"
      >
        <Mic className="size-4" />
        {phone ? 'Try the microphone' : level !== null ? 'Listening…' : 'Test'}
      </button>
    </span>
  );
}
